import { ALL_COPY_ENTITIES, CopyEntity, ExistingMode } from '../types/copy-paste.types';
import { ActionFooter, Section } from '../../../../../shared/styles/components/layout.styles';
import React, { useCallback, useEffect, useState } from 'react';
import { StyledTheatre, TheatreContentDiv, TheatreHeaderDiv } from './CopyPaste.styles';

import Button from '@ingka/button';
import { CloseIconButton } from '../../../../../shared/components/IconButton';
import { CopyOptionsSection } from './CopyOptionsSection';
import { CrossFamilyWarning } from './CrossFamilyWarning';
import type { Item } from '../../../types/item.types';
import { LoadingIndicator } from '../../../../../shared/components/TableLoadingIndicator';
import Modal from '@ingka/modal';
import { ModalCloseButton } from '../../../../../shared/styles/components/modal.styles';
import { SourcePartsTable, TargetItemsTable } from './CopyPasteTables';
import { SubItem } from '../../../types/sub-item.types';
import { canCreateMissingParts, generatePartIdentifier } from '../utils/copy-paste.utils';
import { usePasteSubmit } from '../hooks/usePasteSubmit';

interface PasteTheatreProps {
    copiedParts: SubItem[];
    resolvedSelectedItems: Item[];
    familyName: string | undefined;
    onClose: () => void;
}

export const PasteTheatre = ({ copiedParts, resolvedSelectedItems, familyName, onClose }: PasteTheatreProps) => {
    const [verifiedPartIds, setVerifiedPartIds] = useState<string[]>(
        () => copiedParts.map(generatePartIdentifier)
    );
    const [verifiedItemNos, setVerifiedItemNos] = useState<string[]>(
        () => resolvedSelectedItems.map((i) => i.itemNo)
    );
    const [selectedCopyEntities, setSelectedCopyEntities] = useState<CopyEntity[]>(ALL_COPY_ENTITIES);
    const [existingMode, setExistingMode] = useState<ExistingMode>('overwrite');
    const [createMissing, setCreateMissing] = useState<boolean>(() => canCreateMissingParts(copiedParts));
    const [previewItemNo, setPreviewItemNo] = useState<string>(resolvedSelectedItems[0]?.itemNo ?? '');

    useEffect(() => {
        if (verifiedItemNos.length === 0) {
            if (previewItemNo !== '') setPreviewItemNo('');
            return;
        }
        if (!verifiedItemNos.includes(previewItemNo)) {
            setPreviewItemNo(verifiedItemNos[0]);
        }
    }, [verifiedItemNos, previewItemNo]);

    const { isSubmitting, submitCopyPaste } = usePasteSubmit({
        copiedParts,
        verifiedPartIds,
        verifiedItemNos,
        selectedCopyEntities,
        existingMode,
        createMissing,
        onClose,
    });

    const guardedOnClose = useCallback(() => {
        if (!isSubmitting) onClose();
    }, [isSubmitting, onClose]);

    const canSubmit =
        verifiedPartIds.length > 0 &&
        verifiedItemNos.length > 0 &&
        selectedCopyEntities.length > 0;

    return (
        <Modal visible handleCloseBtn={guardedOnClose}>
            <StyledTheatre aria-label="Paste copied parts" header={
                <TheatreHeaderDiv>
                    <h2>Copied parts{isSubmitting && <LoadingIndicator />}</h2>
                    <div>Paste copied parts to selected items</div>
                    <ModalCloseButton>
                        <CloseIconButton onClick={guardedOnClose} disabled={isSubmitting} />
                    </ModalCloseButton>
                </TheatreHeaderDiv>
            }>
                <TheatreContentDiv>
                    <CrossFamilyWarning
                        resolvedSelectedItems={resolvedSelectedItems}
                        familyName={familyName}
                    />

                    <SourcePartsTable
                        copiedParts={copiedParts}
                        verifiedPartIds={verifiedPartIds}
                        onChangeVerifiedPartIds={setVerifiedPartIds}
                        isSubmitting={isSubmitting}
                    />

                    <TargetItemsTable
                        resolvedSelectedItems={resolvedSelectedItems}
                        verifiedItemNos={verifiedItemNos}
                        onChangeVerifiedItemNos={setVerifiedItemNos}
                        isSubmitting={isSubmitting}
                    />

                    <Section>
                        <CopyOptionsSection
                            copiedParts={copiedParts}
                            resolvedSelectedItems={resolvedSelectedItems}
                            verifiedPartIds={verifiedPartIds}
                            verifiedItemNos={verifiedItemNos}
                            selectedCopyEntities={selectedCopyEntities}
                            onChangeSelectedEntities={setSelectedCopyEntities}
                            existingMode={existingMode}
                            onChangeExistingMode={setExistingMode}
                            createMissing={createMissing}
                            onChangeCreateMissing={setCreateMissing}
                            previewItemNo={previewItemNo}
                            onChangePreviewItemNo={setPreviewItemNo}
                            isSubmitting={isSubmitting}
                        />
                    </Section>

                    <ActionFooter>
                        <Button type='secondary' onClick={guardedOnClose} disabled={isSubmitting}>Cancel</Button>
                        <Button type='emphasised' onClick={submitCopyPaste} disabled={isSubmitting || !canSubmit}>
                            Paste {verifiedPartIds.length} part(s) into {verifiedItemNos.length} item(s)
                        </Button>
                    </ActionFooter>
                </TheatreContentDiv>
            </StyledTheatre>
        </Modal>
    );
};
